import { type ReactNode } from 'react';
import { useServiceGovernance } from '@/hooks/useServiceGovernance';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Lock, ShieldOff } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import type { ServiceKey } from '@/lib/serviceKeys';
import { normalizeServiceGovernanceAvailability } from '@/lib/serviceAvailabilityState';

interface ServicePageGuardProps {
  serviceKey: ServiceKey;
  children: ReactNode;
  /** Override default messages */
  blockedMessage?: string;
}

/**
 * Page-level guard that blocks rendering when a service is disabled.
 * Now governance-aware: differentiates global OFF vs plan-level OFF
 * with distinct icons, titles, and messages.
 */
export function ServicePageGuard({ serviceKey, children, blockedMessage }: ServicePageGuardProps) {
  const governance = useServiceGovernance(serviceKey);
  const { isGlobalOff, message } = governance;
  const availability = normalizeServiceGovernanceAvailability(governance);

  if (availability.state === 'loading') {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!availability.isActionEnabled) {
    const Icon = isGlobalOff ? ShieldOff : Lock;
    const title = isGlobalOff
      ? 'Service Disabled'
      : 'Feature Not Available';

    return (
      <div className="p-8 max-w-lg mx-auto mt-12">
        <Alert className="border-destructive/50 bg-destructive/5">
          <Icon className="h-5 w-5 text-destructive" />
          <AlertTitle className="text-base font-semibold">
            {title}
          </AlertTitle>
          <AlertDescription className="text-sm mt-1">
            {blockedMessage || message || availability.message || 'This feature is not available.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
